import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onValue, ref, update } from 'firebase/database';
import { database } from '../firebase';

type PricingItem = {
  input_text?: number;
  output_text?: number;
  input_image?: number;
  output_image?: number;
  web_search?: number;
  model_id?: string;
};

type PricingDraft = {
  model_id: string;
  input_text: string;
  output_text: string;
  input_image: string;
  output_image: string;
  web_search: string;
};

const decimalPattern = /^\d*(\.\d*)?$/;
type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

const formatDecimal = (value: number) => {
  if (!Number.isFinite(value)) {
    return '';
  }
  const raw = String(value);
  if (!raw.includes('e') && !raw.includes('E')) {
    return raw;
  }

  const [basePart, expPart] = raw.toLowerCase().split('e');
  const exponent = Number(expPart);
  if (!Number.isFinite(exponent)) {
    return raw;
  }

  const sign = basePart.startsWith('-') ? '-' : '';
  const unsignedBase = sign ? basePart.slice(1) : basePart;
  const [intPart, fracPart = ''] = unsignedBase.split('.');
  const digits = `${intPart}${fracPart}`;
  const decimalIndex = intPart.length + exponent;

  if (decimalIndex <= 0) {
    return `${sign}0.${'0'.repeat(Math.abs(decimalIndex))}${digits}`.replace(
      /^-?0\.(0+)$/,
      `${sign}0`
    );
  }

  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${'0'.repeat(decimalIndex - digits.length)}`;
  }

  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
};

function PricingSection() {
  const [pricing, setPricing] = useState<Record<string, PricingDraft>>({});
  const [pricingStatus, setPricingStatus] = useState('Cargando precios...');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [showAddModel, setShowAddModel] = useState(false);
  const [newModelId, setNewModelId] = useState('');
  const saveStatusTimer = useRef<number | null>(null);

  useEffect(() => {
    const pricingRef = ref(database, 'Pricing');
    setPricingStatus('Cargando precios...');
    const unsubscribe = onValue(
      pricingRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, PricingItem> | null;
        if (!value) {
          setPricing({});
          setPricingStatus('No hay modelos configurados.');
          return;
        }

        const nextPricing: Record<string, PricingDraft> = {};
        Object.entries(value).forEach(([modelName, item]) => {
          nextPricing[modelName] = {
            model_id: item.model_id || modelName,
            input_text:
              item.input_text !== undefined ? formatDecimal(item.input_text) : '',
            output_text:
              item.output_text !== undefined ? formatDecimal(item.output_text) : '',
            input_image:
              item.input_image !== undefined ? formatDecimal(item.input_image) : '',
            output_image:
              item.output_image !== undefined ? formatDecimal(item.output_image) : '',
            web_search:
              item.web_search !== undefined ? formatDecimal(item.web_search) : '',
          };
        });
        setPricing(nextPricing);
        setPricingStatus('');
      },
      () => {
        setPricingStatus('Error al cargar precios.');
      }
    );

    return () => unsubscribe();
  }, []);

  const hasPricing = useMemo(() => Object.keys(pricing).length > 0, [pricing]);

  const setSaveStatusFor = (status: SaveStatus, message: string) => {
    setSaveStatus(status);
    setSaveMessage(message);
    if (saveStatusTimer.current) {
      window.clearTimeout(saveStatusTimer.current);
    }
    if (status !== 'saving') {
      saveStatusTimer.current = window.setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 2400);
    }
  };

  const handlePricingChange = (
    modelName: string,
    field: 'input_text' | 'output_text' | 'input_image' | 'output_image' | 'web_search',
    value: string
  ) => {
    if (value !== '' && !decimalPattern.test(value)) {
      setSaveStatusFor('error', 'Solo numeros y decimales.');
      return;
    }
    setPricing((prev) => ({
      ...prev,
      [modelName]: {
        ...prev[modelName],
        [field]: value,
      },
    }));
  };

  const handleSaveAll = async () => {
    const updates: Record<string, number | null> = {};
    let hasError = false;

    Object.entries(pricing).forEach(([modelName, item]) => {
      if (item.input_text === '') {
        updates[`Pricing/${modelName}/input_text`] = null;
      } else {
        const inputTextValue = Number(item.input_text);
        if (!Number.isFinite(inputTextValue)) {
          setSaveStatusFor('error', 'Hay valores inválidos.');
          hasError = true;
          return;
        }
        updates[`Pricing/${modelName}/input_text`] = inputTextValue;
      }

      if (item.output_text === '') {
        updates[`Pricing/${modelName}/output_text`] = null;
      } else {
        const outputTextValue = Number(item.output_text);
        if (!Number.isFinite(outputTextValue)) {
          setSaveStatusFor('error', 'Hay valores inválidos.');
          hasError = true;
          return;
        }
        updates[`Pricing/${modelName}/output_text`] = outputTextValue;
      }

      if (item.input_image === '') {
        updates[`Pricing/${modelName}/input_image`] = null;
      } else {
        const inputImageValue = Number(item.input_image);
        if (!Number.isFinite(inputImageValue)) {
          setSaveStatusFor('error', 'Hay valores inválidos.');
          hasError = true;
          return;
        }
        updates[`Pricing/${modelName}/input_image`] = inputImageValue;
      }

      if (item.output_image === '') {
        updates[`Pricing/${modelName}/output_image`] = null;
      } else {
        const outputImageValue = Number(item.output_image);
        if (!Number.isFinite(outputImageValue)) {
          setSaveStatusFor('error', 'Hay valores inválidos.');
          hasError = true;
          return;
        }
        updates[`Pricing/${modelName}/output_image`] = outputImageValue;
      }

      if (item.web_search === '') {
        updates[`Pricing/${modelName}/web_search`] = null;
      } else {
        const webSearchValue = Number(item.web_search);
        if (!Number.isFinite(webSearchValue)) {
          setSaveStatusFor('error', 'Hay valores inválidos.');
          hasError = true;
          return;
        }
        updates[`Pricing/${modelName}/web_search`] = webSearchValue;
      }
    });

    if (hasError) {
      return;
    }

    if (Object.keys(updates).length === 0) {
      setSaveStatusFor('error', 'No hay cambios para guardar.');
      return;
    }

    setSaveStatusFor('saving', 'Guardando...');
    try {
      await update(ref(database), updates);
      setSaveStatusFor('success', 'Cambios guardados.');
    } catch (saveError) {
      setSaveStatusFor('error', 'Error al guardar.');
    }
  };

  const handleAddModel = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedId = newModelId.trim();

    if (!trimmedId) {
      setSaveStatusFor('error', 'Completa el ID del modelo.');
      return;
    }

    const sanitizedId = trimmedId.replace(/[.\-\s]/g, '_');
    if (!sanitizedId) {
      setSaveStatusFor('error', 'El ID es inválido.');
      return;
    }

    if (pricing[sanitizedId]) {
      setSaveStatusFor('error', 'El modelo ya existe.');
      return;
    }

    setSaveStatusFor('saving', 'Agregando modelo...');
    try {
      await update(ref(database), {
        [`Pricing/${sanitizedId}/model_id`]: trimmedId,
      });
      setPricing((prev) => ({
        ...prev,
        [sanitizedId]: {
          model_id: trimmedId,
          input_text: '',
          output_text: '',
          input_image: '',
          output_image: '',
          web_search: '',
        },
      }));
      setNewModelId('');
      setSaveStatusFor('success', 'Modelo agregado.');
    } catch (addError) {
      setSaveStatusFor('error', 'Error al agregar el modelo.');
    }
  };

  const handleDeleteModel = async (modelName: string) => {
    const confirmed = window.confirm(`¿Eliminar el modelo ${modelName}?`);
    if (!confirmed) {
      return;
    }

    setSaveStatusFor('saving', 'Eliminando modelo...');
    try {
      await update(ref(database), {
        [`Pricing/${modelName}`]: null,
      });
      setSaveStatusFor('success', 'Modelo eliminado.');
    } catch (deleteError) {
      setSaveStatusFor('error', 'Error al eliminar el modelo.');
    }
  };

  return (
    <div className="section">
      <div className="section-header">
        <div>
          <h2>Costos por modelo</h2>
          <div className="hint">
            Costos Gemini:{' '}
            <a
              href="https://ai.google.dev/gemini-api/docs/pricing"
              target="_blank"
              rel="noreferrer"
            >
              https://ai.google.dev/gemini-api/docs/pricing
            </a>
          </div>
        </div>
        {hasPricing ? (
          <div className="panel-actions-inline">
            {saveMessage ? (
              <div className={`inline-toast inline-toast-${saveStatus}`}>{saveMessage}</div>
            ) : null}
            <button
              className="secondary-button"
              type="button"
              onClick={() => setShowAddModel((prev) => !prev)}
              disabled={saveStatus === 'saving'}
            >
              {showAddModel ? 'Ocultar nuevo modelo' : 'Agregar nuevo modelo'}
            </button>
            <button
              className="primary-button"
              type="button"
              onClick={handleSaveAll}
              disabled={saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        ) : null}
      </div>
      {pricingStatus ? <div className="hint">{pricingStatus}</div> : null}
      {showAddModel ? (
        <form className="project-search" onSubmit={handleAddModel}>
          <label className="field">
            <span>ID del modelo</span>
            <input
              type="text"
              value={newModelId}
              onChange={(event) => setNewModelId(event.target.value)}
              placeholder="e.g. gemini-1.5-pro"
            />
          </label>
          <button className="primary-button" type="submit" disabled={saveStatus === 'saving'}>
            Agregar modelo
          </button>
        </form>
      ) : null}
      {hasPricing ? (
        <div className="table">
          <div className="table-row pricing-table-row table-header">
            <div>Model</div>
            <div>Input price por token (txt)</div>
            <div>Output price por token (txt)</div>
            <div>Input price por token (img)</div>
            <div>Output price por token (img)</div>
            <div>SEARCH PRICE PER QUERY (WEB)</div>
            <div>Acciones</div>
          </div>
          {Object.entries(pricing).map(([modelName, item]) => {
            return (
              <div className="table-row pricing-table-row" key={modelName}>
                <div className="mono">{item.model_id || modelName}</div>
                <div>
                  <input
                    className="table-input"
                    type="text"
                    inputMode="decimal"
                    value={item.input_text}
                    onChange={(event) =>
                      handlePricingChange(modelName, 'input_text', event.target.value)
                    }
                    placeholder="0.000000075"
                  />
                </div>
                <div>
                  <input
                    className="table-input"
                    type="text"
                    inputMode="decimal"
                    value={item.output_text}
                    onChange={(event) =>
                      handlePricingChange(modelName, 'output_text', event.target.value)
                    }
                    placeholder="0.000000075"
                  />
                </div>
                <div>
                  <input
                    className="table-input"
                    type="text"
                    inputMode="decimal"
                    value={item.input_image}
                    onChange={(event) =>
                      handlePricingChange(modelName, 'input_image', event.target.value)
                    }
                    placeholder="0.000000075"
                  />
                </div>
                <div>
                  <input
                    className="table-input"
                    type="text"
                    inputMode="decimal"
                    value={item.output_image}
                    onChange={(event) =>
                      handlePricingChange(modelName, 'output_image', event.target.value)
                    }
                    placeholder="0.000000075"
                  />
                </div>
                <div>
                  <input
                    className="table-input"
                    type="text"
                    inputMode="decimal"
                    value={item.web_search}
                    onChange={(event) =>
                      handlePricingChange(modelName, 'web_search', event.target.value)
                    }
                  />
                </div>
                <div>
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => handleDeleteModel(modelName)}
                    disabled={saveStatus === 'saving'}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default PricingSection;
